import { IPublicSession, userSession } from 'state/session';
import { applyRemoteSession } from './applyRemote';
import { getDirtyKeys } from './dirtyKeys';
import { getLastSeenVersion, resetRemoteVersion } from './remoteVersion';
import { sendApiRequest } from 'utils/network';
import logger from 'utils/log';

/**
 * Reconnect read-repair (AVS-4330): after the broadcast socket drops and re-establishes (or the
 * browser comes back online), pull the canonical server record and apply it through the single
 * remote-apply chokepoint. This self-heals any DB-persisted change broadcast while the socket was
 * down. Because it routes through `applyRemoteSession`, the staleness gate and dirty-key
 * protection still hold: a stale record is dropped, and an unflushed local edit is preserved.
 *
 * Uses `sendApiRequest` directly (NOT `loadPublicSession`/`fetchSessionData`): `loadPublicSession`
 * early-returns for the current session id, and `fetchSessionData` caches by id — a repair must
 * fetch fresh. A missed `emitOnly` broadcast (never persisted) is NOT recoverable this way.
 *
 * @returns true when a payload was applied, false on no-op / drop / error. Never rejects and never
 * shows a toast: the degraded slow-poll calls this precisely when the server may be unreachable, so
 * a throw would surface as an unhandled rejection in a timer/listener callback and a toast storm.
 */
export async function repairPublicSession({
  adoptServerVersion = false,
}: {
  /**
   * Set only by the CAS conflict rebase. A 409 proves our baseline is not the server's version, so
   * a record BELOW it means the baseline itself is wrong; without this the gate drops the record
   * and every retry re-sends the same rejected expectedVersion forever (prod 2026-10-15).
   */
  adoptServerVersion?: boolean;
} = {}): Promise<boolean> {
  const id = userSession().get('id');
  if (!id || userSession().get('offline')) {
    return false;
  }

  const raw = await sendApiRequest<IPublicSession>(`/sessions/${id}`, { suppressToast: true }).catch((error) => {
    logger.warn('[Session] Read-repair GET failed', error);
    return undefined;
  });

  // The session changed while the GET was in flight: its record and version belong to the session we left.
  if (!raw || id !== userSession().get('id')) {
    return false;
  }

  const serverVersion = (raw as { version?: number }).version;
  const lastSeenVersion = getLastSeenVersion();
  if (
    adoptServerVersion &&
    typeof serverVersion === 'number' &&
    lastSeenVersion !== undefined &&
    serverVersion < lastSeenVersion
  ) {
    logger.warn('[Session] Conflict rebase: adopting a server version below our baseline', {
      serverVersion,
      lastSeenVersion,
    });
    resetRemoteVersion();
  }

  return applyRemoteSession(raw, { dirtyKeys: getDirtyKeys() });
}
